import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { TOutlineChangeStatus } from "../model/types";
import { usePresentationChatStore } from "./usePresentationChat";

export function dismissChanges(projectId: string) {
  usePresentationChatStore.getState().setChangesDismissedAt(projectId, new Date().toISOString());
}

export function useOutlineChangeStatus(outlineGeneratedAt: string | null, hasSlides: boolean): TOutlineChangeStatus {
  const { id: projectId } = useParams() as { id: string };
  const { selectedProject } = useProjectStore();
  const { projectChapters } = useChapterStore();
  const changesDismissedAt = usePresentationChatStore((s) => s.changesDismissedAt[projectId] || null);

  return useMemo(() => {
    if (!hasSlides) return "no_outline";
    if (!outlineGeneratedAt || !selectedProject) return "no_outline";

    const cutoff = changesDismissedAt
      ? Math.max(new Date(outlineGeneratedAt).getTime(), new Date(changesDismissedAt).getTime())
      : new Date(outlineGeneratedAt).getTime();

    const sourceChanged = projectChapters.some((ch) => {
      const memo = ch.activeMemo;
      if (!memo?.contentUpdatedAt || !memo.mdContent) return false;
      return new Date(memo.contentUpdatedAt).getTime() > cutoff;
    });

    const instructionsChanged = selectedProject.instructionsUpdatedAt
      ? new Date(selectedProject.instructionsUpdatedAt).getTime() > cutoff
      : false;

    if (sourceChanged && instructionsChanged) return "both_changed";
    if (sourceChanged) return "source_changed";
    if (instructionsChanged) return "instructions_changed";
    return "up_to_date";
  }, [hasSlides, outlineGeneratedAt, selectedProject, projectChapters, changesDismissedAt]);
}
